import React, { Suspense, useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { IFixedformAttempt, INextActivity } from "@imaginelearning/mps-api-service";
import { UxAvatarEnum, UxLoading } from "@imaginelearning/mps-ux-component-library";
import { unloadDoolittle } from "components/assignments/course-map-components/utils";
import ProjectLinkPlayer from "components/project-link-player/project-link-player";
import { IScored, IMessage } from "./link-launch-types";
import { RewardContext } from "../../context-providers/reward.provider";
import ActivityTransitionModal from "../modals/activity-transition/activity-transition-modal";
import useGradeRange from "../../hooks/usegraderange";
import LinkLaunchErrorModal from "./link-launch-error-modal";
import { messages, isAssignmentQuiz, isPathwayQuiz, getMessageBasedOnScored } from "./link-launch-messages";
import { logoutUser } from "../../helpers/user-session";
import useUser from "../../hooks/useuser";

interface LinkPlayerWithModalsProps {
  assignmentId?: string;
  lessonId: number | string;
  lessonVersion: number;
  ctkEnvId: number;
  ctkEnvUrl: string;
  avatar: UxAvatarEnum;
  useScoreFromService?: boolean;
  calculatorType?: string;
  updateStatus: (attemptId: string, status: string) => Promise<any>;
  createAttempt: (nextTake: INextActivity) => Promise<IFixedformAttempt>;
  getNextActivity: (lessonId: string, lessonVersion: number, assignmentId: string) => Promise<INextActivity>;
  shouldUseSpeechSettings?: boolean;
}

export interface IActivity {
  takeID: string;
  totalQuestions: number;
  correctAnswers: number;
  pointsEarned: number;
  scorePercent: number;
  isSpanish: boolean;
  lesson?: {
    isCompleted: boolean;
  };
}

function LinkPlayerWithModals({
  assignmentId,
  lessonId,
  lessonVersion,
  ctkEnvId,
  ctkEnvUrl,
  avatar = UxAvatarEnum.JOE,
  useScoreFromService,
  calculatorType,
  updateStatus,
  createAttempt,
  getNextActivity,
  shouldUseSpeechSettings
}: LinkPlayerWithModalsProps): JSX.Element | null {
  if (!createAttempt || !updateStatus || !getNextActivity) {
    throw new Error("Missing required props: createAttempt or updateStatus or getNextActivity");
  }
  const { isKto5, is6to8 } = useGradeRange();

  const [showSpinner, setShowSpinner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);
  const [showError, setShowError] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const playAudio = isKto5 || is6to8;

  const { getReward } = useContext(RewardContext);

  const [currentActivity, setCurrentActivity] = useState<INextActivity | undefined>();
  const [currentAttempt, setCurrentAttempt] = useState<IFixedformAttempt | undefined>();
  const [scored, setScored] = useState<IScored | undefined>(undefined);
  const [status, setStatus] = useState<string>("Start");
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [lessonComplete, setLessonComplete] = useState<boolean>(false);
  const { user, languageSettings } = useUser();

  const [message, setMessage] = useState<IMessage | undefined>();

  unloadDoolittle();

  const exitOutOfHere = () => {
    if (assignmentId) {
      navigate(`/assignments/${assignmentId}`);
    } else {
      // pathways is the current URL, so we force a full reload to remove the link player
      window.location.href = "/pathways";
    }
  };

  const handleErrorClose = () => {
    setShowError(false);
    exitOutOfHere();
  };

  const displaySpinner = () => {
    setShowSpinner(true);
    setShowPlayer(false);
    setShowModal(false);
    setShowError(false);
  };
  const displayPlayer = () => {
    setShowSpinner(true);
    setShowPlayer(true);
    setShowModal(false);
    setShowError(false);
    setPlayerReady(false);
  };
  const displayModal = (msg: IMessage) => {
    setMessage(msg);
    setShowSpinner(false);
    setShowPlayer(false);
    setShowModal(true);
    setShowError(false);
  };
  const displayError = (message: string) => {
    setErrorMessage(message);
    setShowSpinner(false);
    setShowPlayer(false);
    setShowModal(false);
    setShowError(true);
  };

  const displayPauseModal = () => {
    setStatus("Paused");
    displayModal(messages.activityInProgress);
  };

  const hasValue = (value: string | number | undefined) => {
    if (typeof value === "string") {
      return value.trim() !== "";
    }
    if (typeof value === "number") {
      return true;
    }

    return false;
  };

  const getNextActivityToAction = () => {
    // Fetch the next activity
    displaySpinner();
    setCurrentAttempt(undefined);
    getNextActivity(lessonId.toString(), lessonVersion, assignmentId ?? "").then((nextActivity) => {
      setCurrentActivity(nextActivity);
      if (nextActivity.isLessonCompleted) {
        setLessonComplete(true);
      } else {
        setLessonComplete(false);
      }
    });
  };

  const createActivityAttempt = async () => {
    displaySpinner();
    // Create the fixed for attempt
    if (!currentActivity) {
      console.error("No current Activity to create attempt");
      return;
    }
    const attempt = await createAttempt(currentActivity);
    setCurrentAttempt(attempt);
  };

  const updateActivityStatus = async (newStatus: string) => {
    displaySpinner();
    if (!currentActivity) {
      console.error("No current Activity to update status");
      return;
    }
    setScored(undefined); // Clear the old scores
    // Update Status
    updateStatus(currentActivity.takeID, newStatus).then((activity) => {
      if (activity.lesson?.isCompleted) {
        setLessonComplete(true);
      }
      // then set Scored
      if (newStatus === "Completed") {
        getScores(activity);
      }
    });
  };

  const getScores = (activity: IActivity) => {
    if (!activity) {
      setScored(undefined);
      return;
    }

    let scored: IScored = {
      totalQuestions: 0,
      correctAnswers: 0,
      pointsEarned: 0,
      scorePercent: 0
    };
    if (activity.totalQuestions > 0) {
      scored = {
        totalQuestions: Number(activity.totalQuestions),
        correctAnswers: Number(activity.correctAnswers),
        pointsEarned: Number(activity.pointsEarned),
        scorePercent: Number(activity.scorePercent)
      };
      if (useScoreFromService) {
        scored.scorePercent = activity.scorePercent;
      } else if (activity.totalQuestions > 0 && activity.correctAnswers) {
        scored.scorePercent = Math.round((activity.correctAnswers / activity.totalQuestions) * 100);
      }
    }
    if (typeof activity.scorePercent === "string") {
      scored.scorePercent = Number(activity.scorePercent);
    }
    setScored(scored);
  };

  const handleModalClose = async (close = false) => {
    if (close) {
      // Clicked X or Exit - go to Assignments
      exitOutOfHere();
      return;
    }
    if (lessonComplete) {
      exitOutOfHere();
      return;
    }
    if (["CompleteAndExit", "CompleteAndLogout", "CompleteAndTimeout", "ReviewExit"].includes(status)) {
      setStatus("NextActivity");
    } else {
      if (
        (isPathwayQuiz(currentActivity?.activityType ?? "") || isAssignmentQuiz(currentActivity?.activityType ?? "")) &&
        ["start", "nextactivity"].includes(status.toLowerCase())
      ) {
        await createActivityAttempt();
      }
      displayPlayer();
    }
  };

  const handleLinkPlayerError = (error: string) => {
    displayError(error);
  };
  const handleModalExit = () => {
    // Exit the lesson
    exitOutOfHere();
  };

  const handleLinkPlayerReady = () => {
    setPlayerReady(true);
    setShowSpinner(false);
  };

  const handleCloseLinkPlayer = (reason: string) => {
    setStatus(reason);
  };

  const startNextActivity = () => {
    setStatus("NextActivity");
  };

  useEffect(() => {
    if (lessonComplete) {
      getReward(Date.now().toString());
    }
  }, [lessonComplete]);

  useEffect(() => {
    if (user && currentActivity) {
      // When activity changes - this is a start of an activity
      // For Quiz show the modal
      if (isAssignmentQuiz(currentActivity.activityType)) {
        displayModal(messages.beforequiz);
      } else if (isPathwayQuiz(currentActivity.activityType)) {
        displayModal(messages.beforeMasteryCheck);
      } else {
        createActivityAttempt();
      }
    }
  }, [currentActivity, user]);

  useEffect(() => {
    if (currentAttempt) {
      updateActivityStatus("InProgress").then(() => {
        // When Attempt is available
        displayPlayer();
      });
    }
  }, [currentAttempt]);

  useEffect(() => {
    if (scored) {
      const msg = getMessageBasedOnScored(
        scored,
        currentActivity?.activityType ?? "",
        lessonComplete,
        startNextActivity,
        !!assignmentId,
        currentActivity?.isLast
      );
      if (msg) {
        displayModal(msg);
      } else {
        setMessage(undefined);
      }
    }
  }, [scored]);

  useEffect(() => {
    switch (status) {
      case "Logout":
        displaySpinner();
        logoutUser(user);
        return;
      case "CompleteAndExit":
      case "CompleteAndLogout":
      case "CompleteAndTimeout":
      case "ReviewExit":
        updateActivityStatus("Completed");
        break;
      case "Exit":
      case "Timeout":
      case "SaveAndExit":
        displayPauseModal();
        break;
      case "Error":
      case "Unauthorized":
      case "InvalidAttempt":
        displayError("We cannot play this lesson at the moment. Please try again later.");
        break;
      case "NextActivity":
        getNextActivityToAction();
        break;
      default:
        break;
    }
  }, [status]);

  useEffect(() => {
    if (!hasValue(lessonId) || !hasValue(lessonVersion)) return;
    getNextActivityToAction();
  }, [lessonId, lessonVersion, assignmentId]);

  // render
  document.getElementById("doolittle-reader.js")?.remove();
  if (!showError && !showModal && !showPlayer && !showSpinner) {
    return null;
  }

  const spinner = (
    <div className="d-flex justify-content-center" style={{ height: "100vh", marginTop: "15%" }}>
      <Spinner variant="light" animation="border" role="status" style={{ height: "50px", width: "50px" }}>
        <span className="visually-hidden" role="alert">
          Loading...
        </span>
      </Spinner>
    </div>
  );

  if (showSpinner && !showPlayer) {
    return spinner;
  }

  if (showModal && message) {
    return (
      <ActivityTransitionModal
        showModal={showModal}
        isLastMasteryCheck={true}
        message={[
          {
            messageID: undefined,
            message: message.head,
            translationKey: message.head,
            type: 0,
            category: 0
          },
          {
            messageID: undefined,
            message: message.body,
            translationKey: message.body,
            type: 1,
            category: 0
          }
        ]}
        activityStatus={message.activityStatus}
        activityType={message.activityType}
        handleModalClose={handleModalClose}
        handleModalExit={message.canExit ? handleModalExit : undefined}
        avatar={avatar}
        totalScore={scored?.correctAnswers}
        takeTotal={scored?.totalQuestions}
        starsEarned={scored?.pointsEarned ?? 0}
        displayScore={message.showScore}
        scoreOverride={scored?.scorePercent}
        enableAudio={playAudio}
        ActionButtonText="Next"
      />
    );
  }

  if (showError) {
    return (
      <LinkLaunchErrorModal
        handleModalClose={handleErrorClose}
        title={t("PracticeErrorTitle")}
        message={errorMessage ?? t("PracticeErrorMessage")}
      />
    );
  }

  if (showPlayer && currentActivity) {
    return (
      <>
        {!playerReady && spinner}
        <div style={{ display: playerReady ? "block" : "none", height: "100vh", width: "100%" }}>
          <Suspense fallback={<UxLoading />}>
            <ProjectLinkPlayer
              hierarchyId={lessonId.toString()}
              pathId={currentActivity.pathID.toString()}
              pathVersion={currentActivity.version}
              studentId={currentActivity.userID}
              attemptId={currentActivity.takeID}
              onLinkPlayerClose={handleCloseLinkPlayer}
              onLinkPlayerError={handleLinkPlayerError}
              onPlayerReady={handleLinkPlayerReady}
              ctkEnvId={ctkEnvId}
              ctkEnvUrl={ctkEnvUrl}
              calculatorType={calculatorType}
              textToSpeech={shouldUseSpeechSettings && languageSettings?.tts?.math === "on"}
              speechTranslation={shouldUseSpeechSettings && languageSettings?.st?.math === "on"}
            />
          </Suspense>
        </div>
      </>
    );
  }

  return null;
}

export default LinkPlayerWithModals;
